import { useFormContext } from 'react-hook-form';

import { StrategyFormData } from '../../../../../../providers/backtesting/types';
import { CollapsibleSection } from '../../components/collapsible-section';
import { BacktestingInput } from '../../components/backtesting-input';
import { BacktestingSelect } from '../../components/backtesting-select';
import { FormRow } from '../../components/form-row';

import './styles.scss';

const CATEGORY_OPTIONS = [
  { name: 'Structure', value: 'structure' },
  { name: 'Instrument', value: 'instrument' },
];

export const ExitConditionsSection = () => {
  const { register, watch, setValue } = useFormContext<StrategyFormData>();

  const category = watch('exitConditions.category');

  const selectedCategory =
    CATEGORY_OPTIONS.find((o) => o.value === category) ?? CATEGORY_OPTIONS[0];

  const instrumentPvBaseTooltip = () => {
    return (
      <div>
        <p>
          The present value of a unit instrument in base.
          <br />
          <br />
          E.g. 0.0025 (25 bps)
        </p>
      </div>
    );
  };

  const timeValueBaseTooltip = () => {
    return (
      <div>
        <p>
          The time value of a unit instrument in base.
          <br />
          <br />
          E.g. 0.0005 (5 bps)
        </p>
      </div>
    );
  };

  const instrumentDeltaBaseQuoteTooltip = () => {
    return (
      <div>
        <p>
          The delta of a unit instrument in base when hedging into quote (USD).
          <br />
          <br />
          E.g. 0.15 (15-delta call), -0.20 (20-delta put).
        </p>
      </div>
    );
  };

  return (
    <CollapsibleSection title="Exit Conditions" defaultOpen={false}>
      <div className="field-group exit-conditions-category">
        <label className="field-label">Category</label>
        <BacktestingSelect
          values={CATEGORY_OPTIONS}
          defaultValue={selectedCategory}
          onChange={(value) => setValue('exitConditions.category', value, { shouldDirty: true })}
        />
      </div>

      {category === 'structure' && (
        <FormRow layout="flex">
          <div className="field-group">
            <BacktestingInput
              size="sm"
              label="Structure PV (% of Max Intrinsic Value)"
              tooltip={`The PV of the entire structure as a percent of its maximum intrinsic value.`}
              placeholder="Structure PV"
              {...register('exitConditions.structurePvMaxIntrinsic')}
            />
          </div>
          <div className="field-group">
            <BacktestingInput
              size="sm"
              label="Structure PV (% Premium)"
              tooltip={`The PV of the entire structure as a percent of its premium.`}
              placeholder="Structure PV"
              {...register('exitConditions.structurePvPremium')}
            />
          </div>
        </FormRow>
      )}

      {category === 'instrument' && (
        <FormRow columns={5}>
          <div className="field-group">
            <BacktestingInput
              size="fluid"
              label="Instrument PV Base"
              tooltip={instrumentPvBaseTooltip()}
              placeholder="Instrument PV Base"
              {...register('exitConditions.instrumentPvBase')}
            />
          </div>
          <div className="field-group">
            <BacktestingInput
              size="fluid"
              label="Time Value Base"
              tooltip={timeValueBaseTooltip()}
              placeholder="Time Value Base"
              {...register('exitConditions.timeValueBase')}
            />
          </div>
          <div className="field-group">
            <BacktestingInput
              size="fluid"
              label="Instrument Delta Base Quote"
              tooltip={instrumentDeltaBaseQuoteTooltip()}
              placeholder="Instrument Delta Base Quote"
              {...register('exitConditions.instrumentDeltaBaseQuote')}
            />
          </div>
          <div className="field-group">
            <BacktestingInput
              size="fluid"
              label="Strike Vol"
              tooltip={`The strike vol of an option.`}
              placeholder="Strike Vol"
              {...register('exitConditions.strikeVol')}
            />
          </div>
          <div className="field-group">
            <BacktestingInput
              size="fluid"
              label="Time to Expiry"
              tooltip={`The time to expiry of the option.`}
              placeholder="Time to Expiry"
              {...register('exitConditions.timeToExpiry')}
            />
          </div>
          <div className="field-group">
            <BacktestingInput
              size="fluid"
              label="Spot Move Since Inception"
              tooltip={`E.g. Spot(t) > Spot(0) + 1000; Spot(t) < Spot(0)-2000`}
              placeholder="Spot Move Since Inception"
              {...register('exitConditions.spotMoveSinceInception')}
            />
          </div>
          <div className="field-group">
            <BacktestingInput
              size="fluid"
              label="Abs Spot Move Since Inception"
              tooltip={`E.g. |spot(t)-spot(0)| > 1000`}
              placeholder="Abs Spot Move Since Inception"
              {...register('exitConditions.absoluteSpotMoveSinceInception')}
            />
          </div>
        </FormRow>
      )}
    </CollapsibleSection>
  );
};
